import {createApi} from "@reduxjs/toolkit/query/react"
import baseQuery from "../../utils/apiIIKOConfig"
import {IBasicResponse} from "../../types/api"
import {
    IOrderAssignCourierParams,
    IOrderByIdParams,
    IOrderCancellationParams,
    IOrderCloseParams,
    IDeliveryCenterOrdersParams,
    IOrderDeliveredStrictlyParams,
    IOrderDetails,
    IOrderAuditLogItem,
    IOrder,
    IOrdersListParams,
    IOrdersListResponse
} from "./OrderApiTypes"
import {getDeliveryCenterPageDecision} from "./orderPagination"
import {ADMIN_TIME_ZONE} from "../../utils/apiHeaders"

const DELIVERY_CENTER_PAGE_SIZE = 100

export const orderApi = createApi({
    reducerPath: "orderApi",
    baseQuery,
    tagTypes: ["orders"],
    endpoints: (build) => ({
        getOrders: build.query<IBasicResponse<IOrdersListResponse>, IOrdersListParams>({
            query: ({lang, ...body}) => ({
                body,
                url: "v1/orders/list",
                method: "POST",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": ADMIN_TIME_ZONE
                }
            }),
            providesTags: ["orders"]
        }),
        getDeliveryCenterOrders: build.query<IBasicResponse<IOrdersListResponse>, IDeliveryCenterOrdersParams>({
            queryFn: async ({lang, branch_id_list, status_list, date_from, date_to}, _queryApi, _extraOptions, fetchWithBQ) => {
                const orders = new Map<number, IOrder>()
                let page = 1
                let totalElements = 0
                let totalPages = 1
                let responseMeta: Omit<IBasicResponse<IOrdersListResponse>, "data"> = {
                    success: true
                }

                do {
                    const response = await fetchWithBQ({
                        url: "v1/orders/list",
                        method: "POST",
                        headers: {
                            "Accept-Language": lang,
                            "X-Time-Zone": ADMIN_TIME_ZONE
                        },
                        body: {
                            page,
                            size: DELIVERY_CENTER_PAGE_SIZE,
                            type: "DELIVERY",
                            status_list,
                            branch_id_list,
                            date_from,
                            date_to
                        }
                    })

                    if (response.error) {
                        return {error: response.error}
                    }

                    const responseData = response.data as IBasicResponse<IOrdersListResponse>
                    const pageData = responseData.data
                    const content = pageData?.content || []
                    const previousOrderCount = orders.size

                    responseMeta = {
                        success: responseData.success,
                        message: responseData.message
                    }
                    totalElements = pageData?.total_elements || orders.size + content.length
                    totalPages = pageData?.total_pages || Math.max(1, Math.ceil(totalElements / DELIVERY_CENTER_PAGE_SIZE))
                    content.forEach((order) => orders.set(order.id, order))

                    const pageDecision = getDeliveryCenterPageDecision({
                        page,
                        totalPages,
                        pageSize: DELIVERY_CENTER_PAGE_SIZE,
                        receivedCount: content.length,
                        previousOrderCount,
                        currentOrderCount: orders.size,
                        totalElements,
                        success: responseData.success
                    })

                    if (pageDecision.error) {
                        return {
                            error: {
                                status: "CUSTOM_ERROR",
                                error: pageDecision.error
                            }
                        }
                    }

                    if (!pageDecision.shouldContinue) {
                        break
                    }

                    page += 1
                } while (page <= totalPages && orders.size < totalElements)

                return {
                    data: {
                        ...responseMeta,
                        data: {
                            content: Array.from(orders.values()),
                            page: 1,
                            size: orders.size,
                            total_elements: orders.size
                        }
                    }
                }
            }
        }),
        getOrderById: build.query<IBasicResponse<IOrderDetails>, IOrderByIdParams>({
            query: ({lang, id}) => ({
                url: `v1/orders/${id}/detailed`,
                method: "GET",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": ADMIN_TIME_ZONE
                }
            }),
            providesTags: ["orders"]
        }),
        getOrderAuditLogList: build.query<IBasicResponse<IOrderAuditLogItem[]>, IOrderByIdParams>({
            query: ({lang, id}) => ({
                url: `v1/orders/${id}/audit-logs`,
                method: "GET",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": ADMIN_TIME_ZONE
                }
            }),
            providesTags: ["orders"]
        }),
        assignOrderCourier: build.mutation<IBasicResponse<void>, IOrderAssignCourierParams>({
            query: ({lang, id, courierId}) => ({
                url: `v1/orders/${id}/couriers/${courierId}`,
                method: "PATCH",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": ADMIN_TIME_ZONE
                }
            }),
            invalidatesTags: ["orders"]
        }),
        cancelOrder: build.mutation<IBasicResponse<void>, IOrderCancellationParams>({
            query: ({lang, id, comment}) => ({
                url: `v1/orders/${id}/cancellation`,
                method: "PATCH",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": ADMIN_TIME_ZONE
                },
                body: {comment}
            }),
            invalidatesTags: ["orders"]
        }),
        closeOrder: build.mutation<IBasicResponse<void>, IOrderCloseParams>({
            query: ({lang, id}) => ({
                url: `v1/orders/${id}/closing`,
                method: "PATCH",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": ADMIN_TIME_ZONE
                }
            }),
            invalidatesTags: ["orders"]
        }),
        recreateOrder: build.mutation<IBasicResponse<void>, IOrderByIdParams>({
            query: ({lang, id}) => ({
                url: `v1/orders/${id}/recreation`,
                method: "POST",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": ADMIN_TIME_ZONE
                }
            }),
            invalidatesTags: ["orders"]
        }),
        deliverOrderStrictly: build.mutation<IBasicResponse<void>, IOrderDeliveredStrictlyParams>({
            query: ({lang, id, comment}) => ({
                url: `v1/orders/${id}/delivered/strictly`,
                method: "PATCH",
                headers: {
                    "Accept-Language": lang,
                    "X-Time-Zone": ADMIN_TIME_ZONE
                },
                body: {comment}
            }),
            invalidatesTags: ["orders"]
        })
    })
})

export const {
    useGetOrdersQuery,
    useLazyGetDeliveryCenterOrdersQuery,
    useLazyGetOrderByIdQuery,
    useGetOrderByIdQuery,
    useGetOrderAuditLogListQuery,
    useAssignOrderCourierMutation,
    useCancelOrderMutation,
    useCloseOrderMutation,
    useRecreateOrderMutation,
    useDeliverOrderStrictlyMutation
} = orderApi
